import { Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { Profile } from 'src/app/models/profile';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { Events, ModalController, Platform } from '@ionic/angular';
import { ProfileSelectTypeComponent } from 'src/app/components/profile-select-type/profile-select-type.component';
import { ModalContractComponent } from 'src/app/components/modal-contract/modal-contract.component';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { AlertService } from 'src/app/services/alert/alert.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PROFILE_TYPE } from 'src/app/constants';
import { ProfileCreateSuccessComponent } from 'src/app/components/profile-create-success/profile-create-success.component';
import { GeoLocationService } from 'src/app/services/geolocation/geolocation.service';
import { AccountService } from 'src/app/services/auth/account.service';
import jwtDecode from 'jwt-decode';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { RatingsKeys } from 'src/app/models/profile';
import { Storage } from '@ionic/storage';
import { Truck } from 'src/app/models/truck';
import { TruckService } from 'src/app/services/truck/truck.service';
import { InAppBrowserOptions, InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { TranslateService } from '@ngx-translate/core';
import { StripeAccountStatus } from 'src/app/models/stripeAccountStatus';

@Component({
  selector: 'app-profile',
  templateUrl: 'profile.page.html',
  styleUrls: ['profile.page.scss']
})
export class ProfilePage implements OnInit {

  segment = '1';
  profile: Profile;
  modalType;
  trackingEnabled: boolean;
  email: string;
  profileType: PROFILE_TYPE;
  
  trucksFleetBooklet: Truck[] = [];

  hideStripeContent = false;

  constructor(
    private profileService: ProfileService,
    private auth: AuthService,
    private modal: ModalController,
    private spinner: SpinnerService,
    private alert: AlertService,
    private router: Router,
    private geolocation: GeoLocationService,
    private cd: ChangeDetectorRef,
    private account: AccountService,
    private platform: Platform,
    private notification: NotificationService,
    private storage: Storage,
    private truckService: TruckService,
    private events: Events,
    private route: ActivatedRoute,
    private iab: InAppBrowser,
    private translate: TranslateService,
  ) {
    this.events.subscribe('callback-truck', () => { 
      this.getTrucksFleetBooklet() 
    });
  }

  ngOnInit() {
    // Get parameter named segment
    this.route.queryParams.subscribe(params => {
      if (params['segment']) {
        this.segment = params['segment'];
      }
    });

    this.route.fragment.subscribe(fragment => {
      if (fragment) {
        this.segment = fragment;

        const urlWithoutFragment = window.location.pathname + window.location.search;
        window.history.replaceState(null, '', urlWithoutFragment);
      }
    });
  }

  ionViewWillEnter() {
    this.initProfile();
  }

  get isCordova() {
    return this.platform.is('cordova');
  }

  async initProfile() {
    try {
      await this.spinner.show();
      const token = await this.auth.getToken();
      const decoded = jwtDecode(token);
      console.log('Decoded token', decoded);
      this.email = decoded['https://speedlinetrasporti.com/email'];
      console.log('Email from token', this.email);
      this.profile = await this.profileService.me(token);
      this.cd.detectChanges();
      
      if (!this.profile) {
        await this.linkRoleTypeWithProfile();
      }

      this.trackingEnabled = await this.geolocation.isEnabledBackground();
      await this.spinner.dismiss();

      if (this.profile) { // reset tag OneSignal
        if (this.profile.id) {
          if (this.platform.is('cordova')) {
            this.notification.setTag(this.profile.id.toString());
          }
        }
      }
    } catch (err) {
      console.log(err);
      await this.spinner.dismissWithAlertError(err);
    }
  }

  async linkRoleTypeWithProfile() {
    this.storage.get('profileType')
      .then(async (value) => {
        switch(value) {
          case PROFILE_TYPE.private: {
            this.profile = { type: PROFILE_TYPE.private }
            this.profileType = PROFILE_TYPE.private;
            break;
          }
          case PROFILE_TYPE.professional: {
            this.profile = { type: PROFILE_TYPE.professional };
            this.profileType = PROFILE_TYPE.professional;
            break;
          }
          case PROFILE_TYPE.company: {
            this.profile = { type: PROFILE_TYPE.company };
            this.profileType = PROFILE_TYPE.company;
            break;
          }
          default: { // TIP from auth0 but without profile linked in siwego db
            this.openSelectType();
          }
        }
      });
  }
               
  onSubmit(values) {
    if (this.isCreating() && this.isProfessional()) {
      this.openAcceptContract(values);
      return;
    }
    this.save(values);
  }

  async save(values) {
    try {
      await this.spinner.show();
      const token = await this.auth.getToken();
      if (this.isCreating()) {
        const body = { ...values, type: this.profile.type };
        await this.profileService.create(token, body);
        await this.initProfile();
        await this.openCreateSuccess();

        this.removeProfileTypePreferences();
        this.resetProfileOnIonicStorage();
      } else {
        await this.profileService.update(token, values);
        await this.initProfile();
        await this.alert.toastSuccess();

        this.removeProfileTypePreferences();
        this.resetProfileOnIonicStorage();
      }
    } catch (err) {
      await this.spinner.dismissWithAlertError(err);
    }
  }

  async openSelectType() {
    if (!this.modalType) {
      this.modalType = await this.modal.create({
        backdropDismiss: false,
        showBackdrop: false,
        component: ProfileSelectTypeComponent,
      });
    }

    this.modalType.onDidDismiss()
      .then(async ({ data }: { data: { type: PROFILE_TYPE } }) => {
        // TODO capire come fare il logout se non completo il mio profilo
        // if (!data || !data.type) {
        //   this.location.back();
        //   return;
        // }

        this.modalType = null;

        if (!data || !data.type) { // TIP user has closed the modal
          await this.router.navigate(['/tabs/home']);
          return;
        }

        if (data && data.type) { // TIP user has selected the modal options
          let socialProfile = {};
          try {
            socialProfile = await this.auth.getSocialProfile();
            await this.storage.set('profileType', data.type);
            this.profileType = data.type;
            this.profile = { type: data.type, ...socialProfile };
          } catch (e) { }
          this.profile = { type: data.type, ...socialProfile };
        }
      });

    return await this.modalType.present();
  }

  isCreating() {
    if (this.profile != null && this.profile.id != undefined) {
      return false;
    } else {
      return true;
    }
  }
  isPrivate() {
    return this.profile && this.profile.type === PROFILE_TYPE.private;
  }
  isCompany() {
    return this.profile && this.profile.type === PROFILE_TYPE.company;
  }
  isProfessional() {
    return this.profile && this.profile.type === PROFILE_TYPE.professional;
  }
  isStripeConnectAccountReady() {
    return this.profile && this.profile.stripeConnectAccountReady;
  }

  async openAcceptContract(values) {
    const modal = await this.modal.create({
      backdropDismiss: false,
      showBackdrop: false,
      component: ModalContractComponent,
    });

    modal.onDidDismiss()
      .then(({ data }) => {
        if (!data || !data.accept) { return; }

        this.save(values);
      });

    return await modal.present();
  }

  async logout() {
    await this.alert.confirm({
      messageKey: 'LOGOUT_MESSAGE',
      okHandler: async () => {
        this.resetProfileOnIonicStorage();
        this.removeProfileTypePreferences();
        await this.storage.remove('lastUserSearch');  
        
        await this.account.logout(); // TIP set redirect from web browser
        if (this.isCordova) {
          this.router.navigate(['/'], { replaceUrl: true }); // TIP set redirect from app
        }
      }
    });
  }

  async openCreateSuccess() {
    const modal = await this.modal.create({
      backdropDismiss: false,
      showBackdrop: false,
      component: ProfileCreateSuccessComponent,
    });
    return await modal.present();
  }

  async onChangeTracking(event) {
    if (!event.target.checked) {
      this.geolocation.stopBackground();
      return;
    }
    try {
      const authorized = await this.geolocation.isBackgroundAuthorized();
      if (!authorized) {
        throw new Error('Not Authorized');
      }
      await this.geolocation.startBackground();
    } catch (err) {
      event.target.checked = false;
      this.alert.showBackgroundAuthorizeError();
    }
  }

  async onSubscriptionSuccess() {
    await this.initProfile();
    this.router.navigate(['/thankyou-subscription'], { replaceUrl: true });
  }

  segmentChanged(event) {
    this.segment = event.target.value;
  }

  hideRatings() {
    return !this.profile;
  }

  deleteAccount() {
    this.alert.confirm({
      titleKey: 'DELETE_ACCOUNT',
      messageKey: 'DELETE_ACCOUNT_MESSAGE',
      cancelHandler: async () => false, 
      okHandler: async () => {
        try {
          await this.spinner.show();
      
          const token = await this.auth.getToken();
    
          this.removeProfileTypePreferences();
          this.resetProfileOnIonicStorage();

          await this.profileService.delete(token);

          await this.spinner.dismiss();
          
          await this.account.logout(); // TIP set redirect from browser
          this.router.navigate(['/'], { replaceUrl: true }); // TIP set redirect from app
        } catch (err) {
          await this.spinner.dismissWithAlertError(err);
        }
      }
    });
  }

  async removeProfileTypePreferences() {
    await this.storage.remove('profileType');
  }

  async resetProfileOnIonicStorage() {
    await this.storage.remove('onboardingFormSlideOne');
    await this.storage.remove('onboardingFormSlideTwo');
  }

  async jumpFromBackButtonProgrammatically() {
    const lastUserSearch = await this.storage.get('lastUserSearch');

    if (!this.isCreating() && lastUserSearch) {
      await this.router.navigateByUrl(lastUserSearch, { replaceUrl: true });
      await this.storage.remove('lastUserSearch');      
    } else {
      await this.router.navigate(['/tabs/home']);
    }
  }

  async jumpFromIonButtonToHome() {
    await this.router.navigate(['/tabs/home']);
  }

  async getTrucksFleetBooklet() {
    try {
      const token = await this.auth.getToken();

      const body = await this.truckService.getTrucksFleetBooklet(token);
      this.trucksFleetBooklet = body;

    } catch (err) { 
    }
  }

  checkTrucks() {
    if (this.profile && this.profile.id && this.profile.type == PROFILE_TYPE.professional) {
      this.getTrucksFleetBooklet();
    }
  }

  jumpToAddTruck() {
    this.router.navigate(['trucks/add-truck']);
  }

  // Per la registrazione
  async createStripeConnectAccountLink(openInTab: boolean = false) {
    try {
      await this.spinner.show();
      const token = await this.auth.getToken();
      const body = await this.profileService.getStripeConnectAccountLink(token);
      await this.spinner.dismiss();

      const url = body.accountLink;

      if (!this.platform.is('cordova')) {
        if (openInTab) {
          window.open(url, '_blank');
        } else {
          window.open(url, '_self');
        }
        return;
      }
  
      const CLOSE = await this.translate.get('CLOSE').toPromise();
  
      const options: InAppBrowserOptions = {
        location: 'no',
        hidenavigationbuttons: 'yes',
        closebuttoncaption: CLOSE,
        usewkwebview: 'yes',
        cleardata: 'yes',
      };

      const browser = this.iab.create(url, '_blank', options);
      browser.on('loadstart').subscribe(async (e) => {
        if (e.url.includes('success')) {
          browser.close();
          this.onStripeConnectAccountCreationSuccess();
        } else if (e.url.includes('error')) {
          browser.close();
          this.onStripeConnectAccountCreationError();
        } else if (e.url.includes('cancel') || e.url.includes('home')) {
          browser.close();
          this.onStripeConnectAccountCreationError();
        }
      });
    } catch (err) {
      
      await this.spinner.dismissWithAlertError(err);
    }
  }

  async onStripeConnectAccountCreationSuccess() {
    await this.initProfile();
    this.router.navigate(['/profile', ], { replaceUrl: true, queryParams: { segment: '2' } });
  }

  async onStripeConnectAccountCreationError() {
    await this.initProfile();
    this.router.navigate(['/profile'], { replaceUrl: true, queryParams: { segment: '2' } });
  }

  // Per accedere alla pagina di Stripe con i pagamenti
  async createStripeConnectAccountLoginLink() {
    try {
      await this.spinner.show();
      const token = await this.auth.getToken();
      const body = await this.profileService.getStripeConnectAccountLoginLink(token);
      await this.spinner.dismiss();

      const url = body.stripeLoginLink;
      
      if (!this.platform.is('cordova')) {
        const tab = window.open('', '_blank'); // Open a blank tab immediately
        if (tab) {
          tab.location.href = url; // Set the desired URL
        } else {
          console.error('Failed to open new tab, popup blocker might be enabled.');
        }
        // window.open(url, '_blank');
        return;
      }
  
      const CLOSE = await this.translate.get('CLOSE').toPromise();
  
      const options: InAppBrowserOptions = {
        location: 'no',
        hidenavigationbuttons: 'no',
        closebuttoncaption: CLOSE,
        usewkwebview: 'yes',
        cleardata: 'yes',
      };
  
      const browser = this.iab.create(url, '_blank', options);
      browser.on('loadstart').subscribe(async (e) => {
        if (e.url.includes('success')) {
          browser.close();
          this.onStripeConnectAccountCreationSuccess();
        } else if (e.url.includes('error')) {
          browser.close();
          this.onStripeConnectAccountCreationError();
        } else if (e.url.includes('cancel') || e.url.includes('home')) {
          browser.close();
          this.onStripeConnectAccountCreationError();
        }
      });
    } catch (err) {
      
      await this.spinner.dismissWithAlertError(err);
    }
  }
}
